import { useState, useRef } from 'react';
import { Plus } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { templateService } from '../../services/api';
import { Button } from '../../components/ui';
import { LEAD_FIELDS } from './index';

const inputStyle = (dark) => ({
  width: '100%',
  padding: '9px 12px',
  borderRadius: 8,
  border: `1px solid ${dark ? '#475569' : '#D1D5DB'}`,
  background: dark ? '#0F172A' : '#F8FAFC',
  color: dark ? '#F1F5F9' : '#0F172A',
  fontSize: 14,
  outline: 'none',
  boxSizing: 'border-box',
});

const labelStyle = (dark) => ({
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: dark ? '#94A3B8' : '#64748B',
  marginBottom: 5,
});

const TemplateEditor = ({ initial, onSaved, onCancel }) => {
  const { dark } = useTheme();
  const [type, setType] = useState(initial?.type || 'whatsapp');
  const [name, setName] = useState(initial?.name || '');
  const [subject, setSubject] = useState(initial?.subject || '');
  const [body, setBody] = useState(initial?.body || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const bodyRef = useRef(null);

  const sub = dark ? '#64748B' : '#94A3B8';
  const border = dark ? '#334155' : '#E2E8F0';

  const insertField = (key) => {
    const el = bodyRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const before = body.slice(0, start);
    const after = body.slice(end);
    const token = `{{${key}}}`;
    const newBody = before + token + after;
    setBody(newBody);
    setTimeout(() => {
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    }, 0);
  };

  const handleSave = async () => {
    setError('');
    if (!name.trim()) return setError('Template name is required.');
    if (!body.trim()) return setError('Body is required.');
    if (type === 'email' && !subject.trim()) return setError('Email subject is required.');

    setSaving(true);
    try {
      const payload = { name: name.trim(), type, subject: subject.trim(), body: body.trim() };
      if (initial?._id) {
        await templateService.update(initial._id, payload);
      } else {
        await templateService.create(payload);
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save template.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {/* Type selector */}
      <div style={{ display: 'flex', gap: 0, borderRadius: 10, overflow: 'hidden', border: `1px solid ${border}`, marginBottom: 20 }}>
        {[
          { key: 'whatsapp', label: 'WhatsApp', color: '#25D366' },
          { key: 'email',    label: 'Email',    color: '#3B82F6' },
        ].map((t, i) => (
          <button
            key={t.key}
            onClick={() => !initial && setType(t.key)}
            disabled={!!initial}
            style={{
              flex: 1, padding: '11px 0', border: 'none', cursor: initial ? 'default' : 'pointer',
              borderRight: i === 0 ? `1px solid ${border}` : 'none',
              background: type === t.key
                ? (dark ? t.color + '22' : t.color + '15')
                : (dark ? '#0F172A' : '#F8FAFC'),
              color: type === t.key ? t.color : sub,
              fontWeight: type === t.key ? 600 : 500, fontSize: 14,
              transition: 'background 0.15s',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Name */}
      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle(dark)}>Template Name</label>
        <input
          style={inputStyle(dark)}
          placeholder="e.g. Welcome Message, Course Inquiry Follow-up"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>

      {/* Subject (email only) */}
      {type === 'email' && (
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle(dark)}>Email Subject</label>
          <input
            style={inputStyle(dark)}
            placeholder="e.g. Welcome {{leadName}} – Your Interest in {{course}}"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
        </div>
      )}

      {/* Field inserter */}
      <div style={{ marginBottom: 8 }}>
        <label style={labelStyle(dark)}>Insert Field into Body</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
          {LEAD_FIELDS.map(f => (
            <button
              key={f.key}
              onClick={() => insertField(f.key)}
              title={`Insert {{${f.key}}} — example: ${f.example}`}
              style={{
                fontFamily: 'monospace', fontSize: 11,
                background: dark ? '#1E293B' : '#EFF6FF',
                color: dark ? '#93C5FD' : '#2563EB',
                border: `1px solid ${dark ? '#334155' : '#BFDBFE'}`,
                borderRadius: 6, padding: '3px 8px',
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
              }}
            >
              <Plus size={9} />{`{{${f.key}}}`}
            </button>
          ))}
        </div>
      </div>

      {/* Body */}
      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle(dark)}>
          Message Body
          <span style={{ fontWeight: 400, marginLeft: 6, color: sub }}>
            — click a field above or type {'{{variableName}}'} directly
          </span>
        </label>
        <textarea
          ref={bodyRef}
          rows={type === 'email' ? 12 : 8}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={
            type === 'whatsapp'
              ? 'Hello {{leadName}} 👋\n\nThank you for your interest in *{{course}}*...'
              : 'Dear {{leadName}},\n\nThank you for enquiring about {{course}}...'
          }
          style={{
            ...inputStyle(dark),
            resize: 'vertical',
            minHeight: type === 'email' ? 220 : 160,
            lineHeight: 1.65,
            fontFamily: 'monospace',
          }}
        />
      </div>

      {/* Inline variable preview */}
      {body && (
        <div style={{
          marginBottom: 20, padding: '10px 14px',
          background: dark ? '#0F172A' : '#F8FAFC',
          border: `1px solid ${border}`, borderRadius: 10,
        }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: sub, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4 }}>
            Preview with sample values
          </div>
          <pre style={{
            margin: 0, fontSize: 12, lineHeight: 1.65,
            color: dark ? '#CBD5E1' : '#334155',
            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          }}>
            {body.replace(/\{\{(\w+)\}\}/g, (_, key) => {
              const f = LEAD_FIELDS.find(lf => lf.key === key);
              return f ? f.example : `{{${key}}}`;
            })}
          </pre>
        </div>
      )}

      {error && (
        <div style={{
          fontSize: 13, color: '#EF4444', padding: '8px 12px',
          background: dark ? '#1F0A0A' : '#FFF1F2',
          border: `1px solid ${dark ? '#7F1D1D' : '#FECDD3'}`,
          borderRadius: 8, marginBottom: 14,
        }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : initial ? 'Update Template' : 'Create Template'}
        </Button>
      </div>
    </div>
  );
};

export default TemplateEditor;
